/**
 * Zod schemas for the order-level tax exemption.
 * Mirrors `packages/models/src/orders/TaxExemption.ts` — SSOT
 * docs/TAX_EXEMPTION_ARCHITECTURE.md.
 *
 * Two families:
 *   - STORED shapes (`TaxExemptionSchema`, `WaivedTaxSchema`), referenced by
 *     both `OrderSchema` and `OrderDataSchema`. A plain `z.object()` STRIPS
 *     undeclared keys, so an order that is parsed and written back (the offline
 *     projection does exactly that) would otherwise lose its exemption.
 *     Stored maps are first normalized by the defensive model parser, so a
 *     malformed map reads as ABSENT instead of throwing the whole order parse.
 *   - REQUEST bodies for the tax-exemption routes.
 */

import { z } from 'zod';
import {
    OPERATOR_ASSERTION_MAX_LENGTH,
    TAX_EXEMPTION_HOLDER_NAME_MAX,
    TAX_EXEMPTION_NUMBER_MAX,
    TAX_EXEMPTION_REASONS,
    parseTaxExemption,
    parseWaivedTax,
} from '@order-up/models';

import { AppliedTaxSchema } from './AppliedTax';

// ─── Stored shapes ──────────────────────────────────────────────────────────

export const TaxExemptionReasonSchema = z.enum(TAX_EXEMPTION_REASONS);

export const TaxExemptionActorSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('employee'),
        employeeId: z.number(),
        employeeGuid: z.string().optional(),
        name: z.string().min(1),
        verified: z.boolean(),
    }),
    z.object({ kind: z.literal('user'), uid: z.string().min(1), name: z.string().min(1) }),
    z.object({ kind: z.literal('orderup-staff'), uid: z.string().min(1), name: z.string().min(1) }),
]);

export const TaxExemptionDocumentSideSchema = z.enum(['front', 'back']);

export const TaxExemptionDocumentSchema = z.object({
    id: z.string().min(1),
    side: TaxExemptionDocumentSideSchema,
    contentType: z.literal('image/jpeg'),
    sizeBytes: z.number().int().nonnegative(),
    uploadedAt: z.coerce.date(),
    source: z.enum(['upload-link', 'management']),
    uploadedBy: TaxExemptionActorSchema.optional(),
});

export const TaxExemptionEventSchema = z.object({
    action: z.enum(['applied', 'updated', 'removed', 'document_added']),
    at: z.coerce.date(),
    source: z.enum(['pos', 'management', 'upload-link']),
    requestedBy: TaxExemptionActorSchema.optional(),
    approvedBy: TaxExemptionActorSchema.optional(),
    holderName: z.string().max(TAX_EXEMPTION_HOLDER_NAME_MAX).optional(),
    exemptionNumber: z.string().max(TAX_EXEMPTION_NUMBER_MAX).optional(),
    reason: TaxExemptionReasonSchema.optional(),
    documentId: z.string().optional(),
    side: TaxExemptionDocumentSideSchema.optional(),
    idempotencyKey: z.string().max(128).optional(),
});

const TaxExemptionObjectSchema = z.object({
    active: z.boolean(),
    holderName: z.string().max(TAX_EXEMPTION_HOLDER_NAME_MAX).optional(),
    exemptionNumber: z.string().max(TAX_EXEMPTION_NUMBER_MAX).optional(),
    reason: TaxExemptionReasonSchema.optional(),
    appliedAt: z.coerce.date(),
    requestedBy: TaxExemptionActorSchema.optional(),
    approvedBy: TaxExemptionActorSchema,
    removedAt: z.coerce.date().optional(),
    removedBy: TaxExemptionActorSchema.optional(),
    removedRequestedBy: TaxExemptionActorSchema.optional(),
    documents: z.array(TaxExemptionDocumentSchema),
    history: z.array(TaxExemptionEventSchema),
    updatedAt: z.coerce.date(),
});

/** The stored exemption record. Unreadable maps normalize to `undefined`. */
export const TaxExemptionSchema = z.preprocess(
    (value) => parseTaxExemption(value),
    TaxExemptionObjectSchema.optional(),
);

const WaivedTaxObjectSchema = z.object({
    taxableAmount: z.number().min(0),
    taxAmount: z.number().min(0),
    taxes: z.array(AppliedTaxSchema),
});

/** What the tax would have been. `null` (cleared) and malformed normalize to `undefined`. */
export const WaivedTaxSchema = z.preprocess(
    (value) => parseWaivedTax(value),
    WaivedTaxObjectSchema.optional(),
);

export type TaxExemptionSchemaType = z.infer<typeof TaxExemptionObjectSchema>;
export type WaivedTaxSchemaType = z.infer<typeof WaivedTaxObjectSchema>;

// ─── Request bodies ─────────────────────────────────────────────────────────

/** Trimmed, length-capped optional text: empty after trimming is "not given". */
function optionalText(max: number) {
    return z
        .string()
        .transform((v) => v.trim())
        .pipe(z.string().max(max))
        .optional()
        .transform((v) => (v ? v : undefined));
}

const TenantLocationNumericIdsSchema = {
    tenantId: z.coerce.number().int().positive(),
    restaurantId: z.coerce.number().int().positive(),
};

/**
 * The requesting SERVER, as the till names them. The name is never taken from
 * here — only the id, and the id is only trusted as far as the session
 * assertion beside it proves.
 */
const RequesterSchema = {
    requestedByEmployeeId: z.coerce.number().int().optional(),
    requestedByAssertion: z.string().min(1).max(OPERATOR_ASSERTION_MAX_LENGTH).optional(),
};

/** `PUT /orders/:guid/tax-exemption` — apply, or edit details while active. */
export const ApplyTaxExemptionBodySchema = z
    .object({
        ...TenantLocationNumericIdsSchema,
        holderName: optionalText(TAX_EXEMPTION_HOLDER_NAME_MAX),
        exemptionNumber: optionalText(TAX_EXEMPTION_NUMBER_MAX),
        reason: TaxExemptionReasonSchema.optional(),
        ...RequesterSchema,
    })
    .strict();
export type ApplyTaxExemptionBody = z.infer<typeof ApplyTaxExemptionBodySchema>;

/** `POST /orders/:guid/tax-exemption/remove`. */
export const RemoveTaxExemptionBodySchema = z
    .object({ ...TenantLocationNumericIdsSchema, ...RequesterSchema })
    .strict();
export type RemoveTaxExemptionBody = z.infer<typeof RemoveTaxExemptionBodySchema>;

/** `POST /orders/:guid/tax-exemption/upload-link`. */
export const TaxExemptionUploadLinkBodySchema = z.object(TenantLocationNumericIdsSchema).strict();

/**
 * `PATCH /tenants/:t/locations/:r/orders/:guid/tax-exemption` — management
 * edits the details. `null` CLEARS a field; an absent key leaves it unchanged.
 */
export const UpdateTaxExemptionDetailsBodySchema = z
    .object({
        holderName: z
            .union([z.string().transform((v) => v.trim()).pipe(z.string().max(TAX_EXEMPTION_HOLDER_NAME_MAX)), z.null()])
            .optional(),
        exemptionNumber: z
            .union([z.string().transform((v) => v.trim()).pipe(z.string().max(TAX_EXEMPTION_NUMBER_MAX)), z.null()])
            .optional(),
        reason: z.union([TaxExemptionReasonSchema, z.null()]).optional(),
    })
    .strict()
    .refine(
        (b) => b.holderName !== undefined || b.exemptionNumber !== undefined || b.reason !== undefined,
        { message: 'Change at least one field' },
    );
export type UpdateTaxExemptionDetailsBody = z.infer<typeof UpdateTaxExemptionDetailsBodySchema>;

/**
 * Query for the tax-exempt orders report (`GET /reports/tax-exempt-orders`).
 *
 * `startDate` / `endDate` are INSTANTS, not calendar dates: the caller builds
 * the window in the RESTAURANT's timezone (the shared business-timezone helper)
 * and sends its two ends. A bare `YYYY-MM-DD` would be read as UTC midnight —
 * the browser-timezone report bug in another form — so it is refused.
 */
export const TaxExemptOrdersReportQuerySchema = z
    .object({
        tenantId: z.coerce.number().int().positive(),
        restaurantId: z.coerce.number().int().positive(),
        startDate: z.string().datetime({ offset: true }),
        endDate: z.string().datetime({ offset: true }),
    })
    .refine((q) => Date.parse(q.startDate) <= Date.parse(q.endDate), {
        message: 'startDate must not be after endDate',
        path: ['endDate'],
    });
export type TaxExemptOrdersReportQuery = z.infer<typeof TaxExemptOrdersReportQuerySchema>;
